import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import Button from "../../button/button.js";
import Text from "../../typography/text.js";
import "../table-options-cell/styles.css";
import DeleteIcon from "../../../icons/delete-icon.js";
import TickIcon from "../../../icons/tick-icon.js";
import ArrowDown from "../../../icons/arrow-down.js";
import DocumentOutlined from "../../../icons/document-outlined.js";
import { useNavigate } from "react-router-dom";
import { useScreen } from "../../../components/Utils/Screens.js";
import { Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import DropdownPortal from "../../../components/Utils/DropdownPortal.js";
import TrackIcon from "../../../icons/track-icon.js";
import Divider from "../../divider/divider.js";

const GAP = 10;

const TableOptionsCellMenu = (params) => {
  const {
    actionOption,
    actionOptionType,
    optionOne,
    optionOneType,
    optionTwo,
    optionTwoType,
    optionOneClickPath,
    optionOneClickFn,
    optionTwoClickFn,
    optionTwoClickPath,
    optionThree,
    optionThreeType,
    optionThreeClickPath,
    optionThreeClickFn,
    optionFour,
    optionFourType,
    optionFourClickPath,
    optionFourClickFn,
    rowData,
    moreData,
    type,
    openNewTab = false,
    optionDownloadButton,
    optionDownloadButtonType,
    optionDownloadButtonClickFn,
    optionDownloadButtonLoading,
    // Generic, section-grouped menu. When provided, the menu renders these
    // sections instead of the fixed optionOne..optionFour set.
    sections,
    align = "center",
  } = params || {};

  const isSectionMenu = Array.isArray(sections) && sections.length > 0;

  const navigate = useNavigate();
  const [isActionsMenuOpen, setIsActionsMenuOpen] = useState(false);
  const actionButtonRef = useRef(null);
  const menuRef = useRef(null);
  // DropdownPortal renders null on its first pass, so the menu node only shows
  // up a render later. Track it in state so we can re-measure once it exists.
  const [menuNode, setMenuNode] = useState(null);
  const attachMenuRef = (node) => {
    menuRef.current = node;
    setMenuNode(node);
  };
  const [pos, setPos] = useState({ top: 0, left: 0, maxHeight: 0 });

  const positionMenu = () => {
    if (!actionButtonRef.current) return;
    const r = actionButtonRef.current.getBoundingClientRect();
    const vh = window.innerHeight;

    const spaceBelow = vh - r.bottom - GAP;
    const spaceAbove = r.top - GAP;

    // Section menus vary a lot in size, so measure them once rendered instead
    // of relying on the fixed fallback the table menus use.
    const measured = isSectionMenu ? menuRef.current?.getBoundingClientRect() : null;
    const menuHeight = measured?.height || 170;
    const menuWidth = measured?.width || 120;

    // Only flip up when there's not enough room below
    const shouldFlipUp = spaceBelow < menuHeight && spaceAbove > spaceBelow;

    const top = shouldFlipUp
      ? Math.max(GAP, r.top - menuHeight) // position right above button
      : r.bottom; // position below button

    const rawLeft =
      align === "right"
        ? r.right - menuWidth
        : r.left + r.width / 2 - menuWidth / 2;

    // clientWidth excludes the scrollbar, so the menu never tucks under it
    const vw = document.documentElement.clientWidth;
    const left = Math.min(
      Math.max(GAP, rawLeft),
      Math.max(GAP, vw - menuWidth - GAP),
    );

    setPos({
      top,
      left: left,
    });
  };

  const handleActionOptionClick = () => {
    positionMenu();
    setIsActionsMenuOpen((prev) => !prev);
  };

  // Re-position once the section menu is in the DOM and can be measured.
  useLayoutEffect(() => {
    if (isActionsMenuOpen && isSectionMenu && menuNode) positionMenu();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActionsMenuOpen, isSectionMenu, menuNode]);

  // close on outside click/escape
  React.useEffect(() => {
    if (!isActionsMenuOpen) return;
    const onKey = (e) => e.key === "Escape" && setIsActionsMenuOpen(false);

    const onClick = (e) => {
      const btn = actionButtonRef.current;
      const menu = menuRef.current;
      const t = e.target;
      if (btn?.contains(t) || menu?.contains(t)) return;
      setIsActionsMenuOpen(false);
    };

    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onClick);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onClick);
    };
  }, [isActionsMenuOpen]);

  React.useEffect(() => {
    if (!isActionsMenuOpen) return;

    const updatePosition = () => {
      positionMenu();
    };

    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);

    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [isActionsMenuOpen]);

  const isPhone = !useScreen("sm");

  const showActionOptionIcon = useMemo(() => {
    if (
      actionOptionType === "primary" &&
      (actionOption === "Actions" || isSectionMenu)
    ) {
      return <ArrowDown />;
    } else {
      return null;
    }
  }, [actionOptionType, actionOption, isSectionMenu]);

  const showOptionOneIcon = useMemo(() => {
    if (optionOneType === "failure" && optionOne !== "Assign") {
      return <DeleteIcon />;
    } else if (optionOneType === "primary" && optionOne === "Track Order") {
      return <TrackIcon />;
    } else {
      return null;
    }
  }, [optionOneType]);

  const showOptionTwoIcon = useMemo(() => {
    switch (optionTwoType) {
      case "failure":
        return <DeleteIcon />;
      case "success":
        return <TickIcon />;
      default:
        return null;
    }
  }, [optionTwoType]);

  const handleOptionOneClickFn = () => {
    if (typeof optionOneClickFn === "function") {
      optionOneClickFn(rowData);
    } else if (
      optionOneClickPath &&
      type === "CommissionStatementsDistributor"
    ) {
      return navigate(optionOneClickPath, {
        state: {
          ...rowData,
          ...moreData,
        },
      });
    } else if (openNewTab && optionOneClickPath) {
      window.open(optionOneClickPath);
    } else if (optionOneClickPath && !openNewTab) {
      return navigate(optionOneClickPath);
    }
  };

  const handleOptionTwoClickFn = () => {
    if (typeof optionTwoClickFn === "function") {
      optionTwoClickFn(rowData);
    } else if (optionTwoClickPath && type === "CommissionStatementsDetails") {
      return navigate(optionTwoClickPath, {
        state: {
          ...rowData,
          ...moreData,
        },
      });
    } else if (optionTwoClickPath) {
      return navigate(optionTwoClickPath);
    }
  };

  const handleOptionThreeClickFn = () => {
    if (typeof optionThreeClickFn === "function") {
      optionThreeClickFn(rowData);
    } else if (optionThreeClickPath) {
      return navigate(optionThreeClickPath);
    }
  };

  const handleOptionFourClickFn = () => {
    if (typeof optionFourClickFn === "function") {
      optionFourClickFn(rowData);
    } else if (optionFourClickPath) {
      return navigate(optionFourClickPath);
    }
  };

  const handleOptionDownloadButtonClickFn = () => {
    optionDownloadButtonClickFn(rowData);
  };

  return (
    <div>
      <Button
        ref={actionButtonRef}
        variant="table-options-menu-static"
        subVariant={actionOptionType}
        icon={showActionOptionIcon}
        className="button-width-menu"
        onClick={handleActionOptionClick}
      >
        {actionOption}
      </Button>
      {isActionsMenuOpen && (
        <DropdownPortal>
          <div
            ref={attachMenuRef}
            className={
              isSectionMenu
                ? "table-options-cell-menu-dropdown table-options-cell-menu-dropdown-sections"
                : "table-options-cell-menu-dropdown"
            }
            style={{ position: "fixed", top: pos.top, left: pos.left }}
          >
            <div
              className={
                isSectionMenu
                  ? "table-options-cell-menu table-options-cell-menu-sections"
                  : "table-options-cell-menu"
              }
              style={
                isPhone
                  ? {
                      overflowX: "auto",
                      scrollbarWidth: "none",
                    }
                  : {}
              }
            >
              {isSectionMenu
                ? sections.map((section) => {
                    const items = (section?.items || []).filter(Boolean);
                    if (!items.length) return null;
                    return (
                      <div
                        className="table-options-cell-menu-section"
                        key={section.heading}
                      >
                        {section.heading && (
                          <Text variant="tinyText" color="lightText">
                            {section.heading}
                          </Text>
                        )}
                        {items.map((item) => (
                          <Button
                            key={item.label}
                            variant="table-options-menu"
                            subVariant={item.subVariant}
                            icon={item.icon}
                            disabled={Boolean(item.disabled)}
                            title={item.disabled ? item.disabledReason : undefined}
                            className="button-width-menu table-options-cell-menu-item"
                            onClick={() => {
                              setIsActionsMenuOpen(false);
                              item.onClick?.();
                            }}
                          >
                            {item.label}
                          </Button>
                        ))}
                      </div>
                    );
                  })
                : null}
              {!isSectionMenu && (
                <>
              {optionOne && (
                <Button
                  variant="table-options-menu"
                  subVariant={optionOneType}
                  icon={showOptionOneIcon}
                  className="button-width-menu"
                  onClick={handleOptionOneClickFn}
                >
                  {optionOne}
                </Button>
              )}
              {optionDownloadButton && (
                <Button
                  variant="table"
                  subVariant={optionDownloadButtonType}
                  disabled={optionDownloadButtonLoading}
                  className="button-width-menu"
                  onClick={handleOptionDownloadButtonClickFn}
                >
                  {optionDownloadButtonLoading ? (
                    <>
                      <Spin indicator={<LoadingOutlined spin />} />
                    </>
                  ) : (
                    <>
                      <DocumentOutlined className="table-button-icon-left" />{" "}
                      Download
                    </>
                  )}
                </Button>
              )}
              {optionTwo && (
                <Button
                  variant="table-options-menu"
                  subVariant={optionTwoType}
                  icon={showOptionTwoIcon}
                  onClick={handleOptionTwoClickFn}
                  className="button-width-menu"
                >
                  {optionTwo}
                </Button>
              )}
              {optionThree && (
                <Button
                  variant="table-options-menu"
                  subVariant={optionThreeType}
                  // icon={showOptionTwoIcon}
                  onClick={handleOptionThreeClickFn}
                  className="button-width-menu"
                >
                  {optionThree}
                </Button>
              )}
              <Divider />
              {optionFour && (
                <Button
                  variant="table-options-menu"
                  subVariant={optionFourType}
                  // icon={showOptionTwoIcon}
                  onClick={handleOptionFourClickFn}
                  className="button-width-menu"
                >
                  {optionFour}
                </Button>
              )}
                </>
              )}
            </div>
          </div>
        </DropdownPortal>
      )}
    </div>
  );
};

export default TableOptionsCellMenu;
